import React, { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";

import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Modal,
    IconButton,
    CircularProgress,
} from "@mui/material";

import {
    ValueGetterParams,
    ValueFormatterParams,
    ICellRendererParams,
} from "ag-grid-community";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import { User, DEPARTMENTS, ROLES } from "../user";
import { CustomGridColDef, DataGridCustom } from "../../../common/Datagrid";
import { getUsers } from "../routes/getUsers";
import UserForm from "./UserForm";



const DEPARTMENT_MAP = DEPARTMENTS.reduce((map, d) => {
    map[d.id] = d.name;
    return map;
}, {} as Record<number, string>);

// === FUNZIONI UTILI ===
const safeDateValueGetter = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (typeof value === "string") {
        const date = new Date(value.replace("Z", ""));
        return isNaN(date.getTime()) ? null : date;
    }

    return null;
};

const dateFormatter = (params: ValueFormatterParams) => {
    if (!params.value) return "";
    const date = safeDateValueGetter(params.value);
    return date ? format(date, "dd/MM/yyyy HH:mm:ss") : "";
};

const IsActiveRenderer: React.FC<ICellRendererParams> = ({ value }) => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        {value ? (
            <CheckCircleOutlineIcon color="success" fontSize="small" />
        ) : (
            <CancelOutlinedIcon color="error" fontSize="small" />
        )}
    </Box>
);

// === DEFINIZIONE COLONNE ===
const columns: CustomGridColDef[] = [
    { field: "firstName", headerName: "Nome", flex: 1, minWidth: 100, context: { importance: 1 } },
    { field: "lastName", headerName: "Cognome", flex: 1, minWidth: 110, context: { importance: 1 } },

    {
        field: "role",
        headerName: "Ruolo",
        flex: 1,
        minWidth: 120,
        context: { importance: 2 },
        valueGetter: (p: ValueGetterParams<User>) => ROLES[p.data?.role ?? -1] || "Sconosciuto",
    },

    {
        field: "creationDate",
        headerName: "Data creazione",
        flex: 1,
        minWidth: 180,
        context: { importance: 3 },
        valueGetter: (p) => safeDateValueGetter(p.data?.creationDate),
        valueFormatter: dateFormatter,
    },

    {
        field: "lastLogin",
        headerName: "Ultimo accesso",
        flex: 1,
        minWidth: 180,
        context: { importance: 3 },
        valueGetter: (p) => safeDateValueGetter(p.data?.lastLogin),
        valueFormatter: dateFormatter,
    },

    {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        minWidth: 200,
        context: { importance: 2 },
    },

    {
        field: "isActive",
        headerName: "Attivo",
        flex: 0.5,
        minWidth: 100,
        maxWidth: 100,
        context: { importance: 1 },
        cellRenderer: IsActiveRenderer,
    },

    {
        field: "departmentId",
        headerName: "Reparto",
        flex: 1,
        minWidth: 150,
        context: { importance: 3 },
        valueGetter: (p: ValueGetterParams<User>) => {
            const deptId = Number(p.data?.departmentId);
            return DEPARTMENT_MAP[deptId] || "Non Assegnato";
        },
    },
];

// === COMPONENTE PRINCIPALE ===
const title = "Lista Utenti";
const actionTitle = "Aggiungi";

const DataGridComponent: React.FC = () => {
    const [rows, setRows] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState<User | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [search, setSearch] = useState("");

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getUsers();
            const data = await res.json();

            if (res.ok) setRows(data);
            else alert(data.message || "Errore durante il caricamento");
        } catch (e) {
            alert("Errore nel recupero utenti");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRowClick = (row: User) => {
        setCurrentUser(row);
        setIsModalOpen(true);
    };

    const handleAddClick = () => {
        setCurrentUser(undefined); // Reset dello stato dell'utente
        setIsModalOpen(true);      // Apertura modale
    };

    const filteredRows = rows.filter((u) => {
        const s = search.toLowerCase();
        return (
            u.firstName?.toLowerCase().includes(s) ||
            u.lastName?.toLowerCase().includes(s) ||
            u.email?.toLowerCase().includes(s)
        );
    });

    return (
        <Box sx={{ width: "100%", minWidth: 0, p: { xs: 0, sm: 2 } }}>
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, justifyContent: "space-between", mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", whiteSpace: "nowrap", fontSize: { xs: "1rem", sm: "1.1rem" } }}>{title}</Typography>

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, width: { xs: "100%", sm: "auto" } }}>
                        <TextField
                            size="small"
                            placeholder="Cerca..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                            }}
                            sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: "auto" } }}
                        />

                        <Button variant="contained" onClick={() => handleAddClick()} sx={{ width: { xs: "100%", sm: "auto" } }}>
                            {actionTitle}
                        </Button>
                    </Box>
                </Box>

                <Paper sx={{ p: { xs: 0.5, sm: 2 }, height: { xs: "calc(100dvh - 250px)", sm: 600 }, minHeight: 360, position: "relative", overflow: "hidden" }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGridCustom
                            rows={filteredRows}
                            columns={columns}
                            checkboxSelection={false}
                            onRowClick={handleRowClick}
                        />
                    )}
                </Paper>
            </Paper>

            {/* === MODALE === */}
            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 2,
                        p: { xs: 1, sm: 3 },
                        width: { xs: "calc(100vw - 16px)", sm: "fit-content" },
                        minWidth: 0,
                        maxWidth: { xs: "calc(100vw - 16px)", sm: "90vw" },
                        maxHeight: "calc(100dvh - 16px)",
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setIsModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <UserForm
                        twoColumns
                        user={currentUser}
                        refreshUserList={() => {
                            setIsModalOpen(false);
                            fetchData();
                        }}
                    />
                </Box>
            </Modal>
        </Box>
    );
};

export default DataGridComponent;
